import { useState, useMemo } from 'react';
import { Card, Button, Input, message, Modal, Space, Spin, Tag, Row, Col, Form, DatePicker, Select } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { RobotOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { $ } from '../../lib/api';
import { useAuthStore } from '../../store/auth';

/** 任务 B：阶段顺序（L0→L1→...→L7），用于计算合法下一步 */
// 评审#3：补 'L8'(服务期，真实可达 currentStage)——否则 L8 客户 curStageIdx=-1 被误当 <L3：横幅不亮/下拉全禁/静默提交，与后端 fromOrder>=L3 口径不一致
const STAGE_SEQ = ['L0', 'L1', 'L2', 'L3', 'L4', 'L5a', 'L5b', 'L6', 'L7', 'L8'];

/** 任务 A：渲染前 hashSet dedup，避免 LLM 重复条目 */
function dedupeScripts(list: any[]): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const s of list ?? []) {
    const key = (s?.content ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

export function NewFollowUpPage() {
  const [sp] = useSearchParams();
  const customerId = Number(sp.get('customer'));
  const nav = useNavigate();
  const me = useAuthStore((s) => s.user);
  const [chatLog, setChatLog] = useState('');
  const [analysis, setAnalysis] = useState<any>(null);
  const [content, setContent] = useState('');
  const [form] = Form.useForm();

  const { data: customer } = useQuery({
    queryKey: ['customer', String(customerId)],
    queryFn: () => $.get(`/customers/${customerId}`),
    enabled: !!customerId,
  });

  // 任务 C2：监听 stage 字段，L0/L1/L2 时禁用"下次沟通时间"
  const watchedStage = Form.useWatch('stage', form) as string | undefined;
  const isAutoNextContact = !!watchedStage && ['L0', 'L1', 'L2'].includes(watchedStage);
  // 口径1：分界=L3，只看起点——L3+ 客户的【任意】阶段变动(前进/降级/跳级)都走主管审核；L2→L3 及 <L3 不审、立即生效
  const curStageIdx = STAGE_SEQ.indexOf(customer?.currentStage ?? 'L1');
  const toStageIdx = watchedStage ? STAGE_SEQ.indexOf(watchedStage) : -1;
  const isL3PlusCustomer = curStageIdx >= STAGE_SEQ.indexOf('L3');
  const needsStageApproval = isL3PlusCustomer && toStageIdx >= 0 && toStageIdx !== curStageIdx;
  // 评审#7：横幅文案按方向区分——前进保留 1h 软超时，降级/跳级纯硬卡无超时(与后端 autoApproveAt 一致)
  const stageDir = needsStageApproval
    ? (toStageIdx < curStageIdx ? '降级' : (toStageIdx - curStageIdx > 1 ? '跳级' : '前进'))
    : null;
  const stageApprovalNote = stageDir === '前进' ? '1h 未审自动通过' : '无超时，需主管处理';

  // 任务 B：阶段下拉按当前 stage + role 动态生成
  const stageOptions = useMemo(() => {
    const cur: string = customer?.currentStage ?? 'L1';
    const ci = STAGE_SEQ.indexOf(cur);
    const isPriv = me && ['manager', 'director', 'admin'].includes(me.role);
    const isL3Plus = ci >= STAGE_SEQ.indexOf('L3'); // 口径1：L3+ 客户任意阶段变动走主管审核(含降级/跳级)，全员可选可提交
    return STAGE_SEQ
      .filter((s) => s !== 'L0') // L0 是加微信前阶段，不应在跟进表单出现
      .map((s) => {
        const si = STAGE_SEQ.indexOf(s);
        const delta = si - ci;
        const isLinear = delta === 0 || delta === 1;
        // L3+ 客户：所有阶段可选(降级/跳级也放开、走审核)；<L3：保持线性限制(sales)/priv 可 override
        const allowed = isL3Plus || isLinear || isPriv;
        const label = isL3Plus && delta !== 0
          ? (delta < 0 ? `${s}（降级，需主管审核）` : delta > 1 ? `${s}（跳级，需主管审核）` : `${s}（需主管审核）`)
          : (isLinear ? s : delta < 0 ? `${s}（倒退，主管确认）` : `${s}（跳级，主管确认）`);
        return { value: s, label, disabled: !allowed };
      });
  }, [customer?.currentStage, me?.role]);

  const analyze = useMutation({
    mutationFn: () => $.post('/follow-ups/analyze', { customerId, chatLog }),
    onSuccess: (r: any) => {
      setAnalysis(r);
      setContent(r.summary + '\n\n' + (r.recommendedScripts?.map((s: any) => `· ${s.title}：${s.content}`).join('\n') || ''));
      form.setFieldsValue({
        stage: r.currentStageSuggestion,
        nextFollowupAt: r.nextFollowupAt ? dayjs(r.nextFollowupAt) : null,
      });
      message.success('AI 分析完成');
    },
  });

  const save = useMutation({
    mutationFn: (v: any) =>
      $.post('/follow-ups', {
        customerId,
        content,
        chatLogRaw: chatLog,
        stage: v.stage,
        nextFollowupAt: v.nextFollowupAt?.toISOString(),
        aiAnalysisJson: analysis,
        aiSummary: analysis?.summary,
        aiMood: analysis?.customerMood,
        applyAiSuggestion: !!analysis, // 评审 inc5：仅有 AI 分析时才 true（语义准确；无分析=手动，走 dto.stage）
        forceStageOverride: !!v.forceStageOverride,
      }),
    onSuccess: (r: any) => {
      // L3-B(batch-7): 后端返回 _warnings 时显黄字提醒(不阻断已成功的保存)
      if (r?._warnings?.length) message.warning(`已保存。${r._warnings.join('；')}`, 6);
      else message.success('已保存');
      nav(`/customers/${customerId}`);
    },
  });

  // 任务 B：保存前判断 stage 是否跳/倒退；manager+ 二次确认
  const handleSave = (v: any) => {
    const cur = customer?.currentStage ?? 'L1';
    const ci = STAGE_SEQ.indexOf(cur);
    const si = STAGE_SEQ.indexOf(v.stage);
    const delta = si - ci;
    // 口径1：L3+ 客户的任意阶段变动 → 走主管审核(后端建 stage_advance 单)，直接提交、不需 forceStageOverride
    if (ci >= STAGE_SEQ.indexOf('L3') && si >= 0 && delta !== 0) {
      const dir = delta < 0 ? '降级' : (delta > 1 ? '跳级' : '前进');
      Modal.confirm({
        title: `阶段${dir}需主管审核`,
        content: `客户当前 ${cur} → ${v.stage}（${dir}）。提交后需主管审核通过才生效${dir === '前进' ? '（或 1 小时自动通过）' : '（无超时，主管不点则一直待审）'}。确认提交？`,
        okText: '提交审核', cancelText: '取消',
        onOk: () => save.mutate(v),
      });
      return;
    }
    // <L3 区间：原逻辑(线性立即生效；非线性 sales 拦、priv 二确 override)
    const isLinear = ci < 0 || si < 0 || delta === 0 || delta === 1;
    if (isLinear) return save.mutate(v);
    const isPriv = me && ['manager', 'director', 'admin'].includes(me.role);
    if (!isPriv) {
      message.error('阶段必须顺序推进，禁止跳级/倒退');
      return;
    }
    Modal.confirm({
      title: delta < 0 ? '阶段倒退确认' : '阶段跳级确认',
      content: `客户当前 ${cur}，本次跟进将跳到 ${v.stage}（${delta < 0 ? '倒退' : '跳级'} ${Math.abs(delta)} 级）。该操作会写入审计日志，确认提交？`,
      okText: '确认提交',
      cancelText: '取消',
      onOk: () => save.mutate({ ...v, forceStageOverride: true }),
    });
  };

  return (
    <div>
      <Card title={
        <div>
          新增跟进 {customer && <span style={{ color: 'var(--text-3)', fontSize: 13 }}>· {customer.nickname} ({customer.currentStage})</span>}
        </div>
      } extra={<Link to={`/customers/${customerId}`}>返回客户</Link>}>
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 500 }}>
              📋 把聊天记录粘贴进来，AI 帮你写
              <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-3)' }}>
                （这是 HuanKe-CRM 的核心功能）
              </span>
            </div>
            <Input.TextArea
              rows={12}
              value={chatLog}
              onChange={(e) => setChatLog(e.target.value)}
              placeholder="从微信复制聊天记录粘到这里，AI 会自动写跟进摘要、提取价格/时间/痛点、推断阶段、推荐话术..."
            />
            <Button
              type="primary"
              icon={<RobotOutlined />}
              loading={analyze.isPending}
              style={{ marginTop: 12 }}
              disabled={chatLog.length < 10}
              onClick={() => analyze.mutate()}
              block
            >
              AI 分析（5-10 秒）
            </Button>
          </Col>

          <Col span={12}>
            <Form form={form} layout="vertical">
              <TemplatePicker
                stage={customer?.currentStage}
                onPick={(t: any) => setContent((c) => c ? `${c}\n\n${t.content}` : t.content)}
              />
              <Form.Item label="跟进内容（可基于 AI 输出修改）" required>
                <Input.TextArea
                  rows={6}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="至少 20 字"
                />
              </Form.Item>
              <Form.Item
                name="stage"
                label={
                  <>
                    阶段 <span style={{ fontSize: 11, color: 'var(--text-3)' }}>当前 {customer?.currentStage ?? '—'}</span>
                    {needsStageApproval && (
                      <span style={{ fontSize: 11, color: 'var(--warning-text, #ad6800)', marginLeft: 8 }}>
                        ⚠️ 阶段{stageDir}需主管审核通过才生效（{stageApprovalNote}）
                      </span>
                    )}
                  </>
                }
                rules={[{ required: true, message: '请选择跟进阶段' }]}
              >
                <Select options={stageOptions} placeholder="选择阶段" />
              </Form.Item>
              <Form.Item
                name="nextFollowupAt"
                label={
                  <>
                    下次沟通时间
                    {isAutoNextContact && (
                      <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 8 }}>
                        按 {watchedStage} 阈值自动设定（员工不可改）
                      </span>
                    )}
                    {!isAutoNextContact && !!watchedStage && (
                      <span style={{ fontSize: 11, color: 'var(--warning-text, #ad6800)', marginLeft: 8 }}>
                        {needsStageApproval
                          ? `⚠️ 随本次阶段${stageDir}一起审核，主管通过后才生效（${stageApprovalNote}）`
                          : '⚠️ 提交后需主管审核通过才生效（12h 未审自动通过）'}
                      </span>
                    )}
                  </>
                }
                rules={isAutoNextContact ? [] : [{ required: true, message: '请选择下次沟通时间' }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  disabled={isAutoNextContact}
                  placeholder={isAutoNextContact ? '保存后系统按阈值自动算' : undefined}
                />
              </Form.Item>
              <Button
                type="primary"
                size="large"
                block
                disabled={content.length < 20}
                loading={save.isPending}
                onClick={() => form.validateFields().then(handleSave)}
              >
                保存跟进
              </Button>
            </Form>
          </Col>
        </Row>

        {analysis && (
          <Card type="inner" title={<span><RobotOutlined /> AI 分析结果</span>} style={{ marginTop: 24 }}>
            <Row gutter={16}>
              <Col span={12}>
                <div className="ai-box">
                  <div className="ai-box-head"><RobotOutlined /><span className="ai-box-title">摘要</span></div>
                  <div className="ai-box-text">{analysis.summary}</div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <Tag color="blue">推断阶段：{analysis.currentStageSuggestion}</Tag>
                  <Tag color={analysis.customerMood === 'positive' ? 'green' : 'orange'}>情绪：{analysis.customerMood}</Tag>
                  <Tag color="purple">质量分：{analysis.qualityScore}/5</Tag>
                  <Tag color={analysis.healthScoreDelta >= 0 ? 'green' : 'red'}>健康度{analysis.healthScoreDelta >= 0 ? '+' : ''}{analysis.healthScoreDelta}</Tag>
                </div>
                {analysis.extractedInfo && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>提取信息：</div>
                    {analysis.extractedInfo.mentionedPrice && <div>💰 价格：{analysis.extractedInfo.mentionedPrice}</div>}
                    {analysis.extractedInfo.mentionedTime && <div>⏰ 时间：{analysis.extractedInfo.mentionedTime}</div>}
                    {analysis.extractedInfo.painPoints?.length > 0 && <div>🎯 痛点：{analysis.extractedInfo.painPoints.join('、')}</div>}
                    {analysis.extractedInfo.objections?.length > 0 && <div>🤔 异议：{analysis.extractedInfo.objections.join('、')}</div>}
                    {analysis.extractedInfo.competitorsMentioned?.length > 0 && <div>⚔️ 竞品：{analysis.extractedInfo.competitorsMentioned.join('、')}</div>}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>推荐话术（点击复制）：</div>
                {dedupeScripts(analysis.recommendedScripts ?? []).map((s: any, i: number) => (
                  <Card size="small" key={i} style={{ marginBottom: 8 }}
                    title={<><Tag color="purple">{s.title}</Tag><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{s.reason}</span></>}
                    extra={<Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(s.content); message.success('已复制'); }}>复制</Button>}>
                    <div style={{ fontSize: 13, lineHeight: 1.6 }}>{s.content}</div>
                  </Card>
                ))}
              </Col>
            </Row>
          </Card>
        )}
      </Card>
    </div>
  );
}

/** P2-9 跟进模板选择器 */
function TemplatePicker({ stage, onPick }: { stage?: string; onPick: (t: any) => void }) {
  const { data: templates } = useQuery({
    queryKey: ['follow-up-templates', stage],
    queryFn: () => $.get<any[]>('/follow-up-templates', stage ? { stage } : {}),
    enabled: true,
  });
  if (!templates || templates.length === 0) return null;
  return (
    <Form.Item label={<>跟进模板 <span style={{ fontSize: 11, color: 'var(--text-3)' }}>点击插入到跟进内容</span></>}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {templates.slice(0, 8).map((t: any) => (
          <Tag
            key={t.id}
            color={t.isOfficial ? 'purple' : 'default'}
            style={{ cursor: 'pointer', padding: '4px 10px' }}
            onClick={async () => {
              onPick(t);
              // 增加 use_count（非阻塞）
              $.put(`/follow-up-templates/${t.id}/use`).catch(() => {});
            }}
            title={t.content}
          >
            {t.stage ? `${t.stage} · ` : ''}{t.title}
          </Tag>
        ))}
      </div>
    </Form.Item>
  );
}
